import moment from 'moment'
import getStorageSizeInBytesByUnit from '@/utils/storageSizes'

export async function isoUtcNow () {
  const now = moment.utc().format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z'
  return now
}

export async function topUpUrl (baseUrl) {
  return baseUrl
}

export async function initUrl (baseUrl) {
  return baseUrl
}

export async function updateUrl (sessionId, baseUrl) {
  return `${baseUrl}/${sessionId}/update`
}

export async function terminateUrl (sessionId, baseUrl) {
  return `${baseUrl}/${sessionId}/release`
}

export async function headers (idToken) {
  return {
    Authorization: `Bearer ${idToken}`
  }
}

export async function topUpData (providerId, deviceId, volume) {
  return {
    invocationSequenceNumber: 1,
    tenantIdentifier: providerId,
    subscriberIdentifier: deviceId,
    multipleUnitUsage: [
      {
        requestedUnit: {
          serviceSpecificUnits: volume
        },
        ratingGroup: 400
      }
    ],
    pDUSessionChargingInformation: {
      userInformation: {
        servedGPSI: deviceId,
        unauthenticatedFlag: false
      }
    },
    oneTimeEvent: true,
    oneTimeEventType: 'IEC',
    requestedAction: 'TOP_UP',
    nfConsumerIdentification: {
      nodeFunctionality: 'SMF'
    },
    invocationTimeStamp: await isoUtcNow()
  }
}

export async function initData (
  providerId, deviceId, callednumber, volume, ratingGroupNumber,
  unitType, mcc, mnc, carrierSelectRoutingInformation, numberPortabilityRoutingInformation
) {
  return {
    invocationSequenceNumber: 1,
    tenantIdentifier: providerId,
    subscriberIdentifier: deviceId,
    multipleUnitUsage: [
      {
        requestedUnit: {
          [unitType]: volume
        },
        ratingGroup: ratingGroupNumber
      }
    ],
    pDUSessionChargingInformation: {
      userInformation: {
        servedGPSI: deviceId,
        unauthenticatedFlag: false
      },
      pduSessionInformation: {
        servingNetworkFunctionID: {
          servingNetworkFunctionInformation: {
            nFPLMNID: {
              mcc: mcc,
              mnc: mnc
            },
            nodeFunctionality: 'SMF'
          }
        },
        dnnId: 'dnn.example.com'
      },
      uetimeZone: '+00:00'
    },
    iMSChargingInformation: {
      calledPartyAddress: callednumber,
      carrierSelectRoutingInformation: carrierSelectRoutingInformation || 'ARIN',
      numberPortabilityRoutingInformation: numberPortabilityRoutingInformation || 'ARIN'
    },
    locationReportingChargingInformation: {
      pSCellInformation: {
        nrcgi: {
          nrCellId: '11',
          nid: '12',
          plmnId: {
            mcc: mcc,
            mnc: mnc
          }
        }
      }
    },
    nfConsumerIdentification: {
      nodeFunctionality: 'SMF'
    },
    invocationTimeStamp: await isoUtcNow()
  }
}

export function getLogTimeSummary (logTimeRaw = moment.utc()) {
  return logTimeRaw.clone().subtract(1, 'seconds').format('YYYY-MM-DDTHH:mm:ss.SS') + 'Z'
}

export function getLogTimeVerbose (logTimeRaw = moment.utc()) {
  return logTimeRaw.format('YYYY-MM-DDTHH:mm:ss.SS') + 'Z'
}

export function getGrantedVolume (ratingGroup, grantedUnit, state) {
  switch (ratingGroup) {
    case (state.voiceRatingGroup || 100): return grantedUnit.time
    case (state.textRatingGroup || 200): return grantedUnit.serviceSpecificUnits
    case (state.dataRatingGroup || 300):
    case state.netflixRatingGroup:
    case state.instagramRatingGroup:
    case state.podcastRatingGroup:
    case state.whatsappRatingGroup:
      return grantedUnit.totalVolume
    case 400: return grantedUnit.serviceSpecificUnits
    case 340:
    case 380:
      return grantedUnit.totalVolume
    default:
      if (ratingGroup > 300 && ratingGroup < 400) {
        return grantedUnit.totalVolume || grantedUnit.serviceSpecificUnits
      }
      console.error('check Granted Volume ratingGroup', ratingGroup, 'unit is', JSON.stringify(grantedUnit))
      return -1
  }
}

export function getRatingGroupName (ratingGroup, state) {
  switch (ratingGroup) {
    case (state.voiceRatingGroup || 100): return 'voice'
    case (state.textRatingGroup || 200): return 'sms'
    case (state.dataRatingGroup || 300):
    case state.netflixRatingGroup:
    case state.instagramRatingGroup:
    case state.podcastRatingGroup:
    case state.whatsappRatingGroup:
    case 340:
    case 380:
      return 'data'
    default:
      return ratingGroup > 300 && ratingGroup < 400 ? 'data' : 'unknown'
  }
}

export function getUnitFancy (ratingGroup, volume, state) {
  const MINUTE = 60
  switch (ratingGroup) {
    case (state.voiceRatingGroup || 100): return `${volume / MINUTE} min`
    case (state.textRatingGroup || 200): return `${volume} SMS`
    case (state.dataRatingGroup || 300):
    case state.netflixRatingGroup:
    case state.instagramRatingGroup:
    case state.podcastRatingGroup:
    case state.whatsappRatingGroup:
    case 340:
    case 380:
      return `${volume / getStorageSizeInBytesByUnit('MB')} MB`
    default:
      return `ratingGroup ${ratingGroup} ${volume}`
  }
}

export async function updateData (
  providerId, callednumber, deviceId, volume, ratingGroupNumber,
  unitType, mcc, mnc, carrierSelectRoutingInformation, numberPortabilityRoutingInformation
) {
  const timestampNow = await isoUtcNow()
  return {
    invocationSequenceNumber: 2,
    tenantIdentifier: providerId,
    subscriberIdentifier: deviceId,
    multipleUnitUsage: [
      {
        requestedUnit: {
          [unitType]: 0
        },
        usedUnitContainer: [
          {
            localSequenceNumber: 1,
            [unitType]: volume
          }
        ],
        ratingGroup: ratingGroupNumber
      }
    ],
    pDUSessionChargingInformation: {
      userInformation: {
        servedGPSI: deviceId,
        unauthenticatedFlag: false
      },
      pduSessionInformation: {
        servingNetworkFunctionID: {
          servingNetworkFunctionInformation: {
            nFPLMNID: {
              mcc: mcc,
              mnc: mnc
            },
            nodeFunctionality: 'SMF'
          }
        },
        dnnId: 'dnn.example.com'
      },
      uetimeZone: '+00:00'
    },
    iMSChargingInformation: {
      calledPartyAddress: callednumber,
      carrierSelectRoutingInformation: carrierSelectRoutingInformation || 'ARIN',
      numberPortabilityRoutingInformation: numberPortabilityRoutingInformation || 'ARIN'
    },
    locationReportingChargingInformation: {
      pSCellInformation: {
        nrcgi: {
          nrCellId: '11',
          nid: '12',
          plmnId: {
            mcc: mcc,
            mnc: mnc
          }
        }
      }
    },
    nfConsumerIdentification: {
      nodeFunctionality: 'SMF'
    },
    invocationTimeStamp: timestampNow
  }
}

export async function terminateData (
  providerId, callednumber, deviceId, ratingGroupNumber,
  unitType, mcc, mnc, carrierSelectRoutingInformation, numberPortabilityRoutingInformation
) {
  const timestampNow = await isoUtcNow()
  return {
    invocationSequenceNumber: 3,
    tenantIdentifier: providerId,
    subscriberIdentifier: deviceId,
    multipleUnitUsage: [
      {
        usedUnitContainer: [
          {
            localSequenceNumber: 2,
            [unitType]: 0
          }
        ],
        ratingGroup: ratingGroupNumber
      }
    ],
    pDUSessionChargingInformation: {
      userInformation: {
        servedGPSI: deviceId,
        unauthenticatedFlag: false
      },
      pduSessionInformation: {
        servingNetworkFunctionID: {
          servingNetworkFunctionInformation: {
            nFPLMNID: {
              mcc: mcc,
              mnc: mnc
            },
            nodeFunctionality: 'SMF'
          }
        },
        dnnId: 'dnn.example.com'
      },
      uetimeZone: '+00:00'
    },
    iMSChargingInformation: {
      calledPartyAddress: callednumber,
      carrierSelectRoutingInformation: carrierSelectRoutingInformation || 'ARIN',
      numberPortabilityRoutingInformation: numberPortabilityRoutingInformation || 'ARIN'
    },
    locationReportingChargingInformation: {
      pSCellInformation: {
        nrcgi: {
          nrCellId: '11',
          nid: '12',
          plmnId: {
            mcc: mcc,
            mnc: mnc
          }
        }
      }
    },
    nfConsumerIdentification: {
      nodeFunctionality: 'SMF'
    },
    invocationTimeStamp: timestampNow
  }
}
